import { welfordAdd } from '/bb/aggregators/welford.js';
import { canDetectAutocompleteOnInput, isAutocompleteEligibleField, resolveField } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateField, getOrCreateForm, markFieldChange } from '/bb/helpers.js';
import { AUTOFILL_BATCH_WINDOW_MS, AUTOFILL_VALUE_JUMP_MIN, RANGE_GESTURE_DUPLICATE_GRACE_MS } from '/bb/constants.js';
import { isWithinAutocompleteBatch, recordAutocompleteAcceptance, startAutocompleteBatch } from '/bb/autocomplete.js';

const CORRECTION_INPUT_TYPES = new Set([
  'deleteContentBackward',
  'deleteContentForward',
  'deleteByCut',
  'deleteByDrag',
  'deleteWordBackward',
  'deleteWordForward',
  'historyUndo',
]);

let dateCorrectionSessions = new WeakMap();

export function startDateCorrectionSession(field, fld, form, target) {
  if (!field || field.type !== 'date' || !target) return;
  if (dateCorrectionSessions.has(target)) return;

  dateCorrectionSessions.set(target, {
    baselineValue: dateValue(target),
    baselineInputAt: fld.runtime.lastInputAt,
    previousFormInputFieldName: form.runtime.lastInputFieldName,
    sawInput: false,
  });
}

export function peekDateCorrectionSession(target) {
  return dateCorrectionSessions.get(target);
}

export function restoreDateCorrectionSession(target, session) {
  if (session) dateCorrectionSessions.set(target, session);
  else dateCorrectionSessions.delete(target);
}

export function finalizeDateCorrectionSession(field, fld, form, target, t = Date.now()) {
  if (!field || field.type !== 'date' || !target) return;

  const session = dateCorrectionSessions.get(target);
  dateCorrectionSessions.delete(target);
  if (!session || !session.sawInput || session.baselineValue === '') return;

  const finalValue = dateValue(target);
  if (finalValue === session.baselineValue) return;

  recordCorrectionTiming(form, fld, {
    t,
    lastInputAt: session.baselineInputAt,
    lastInputFieldName: session.previousFormInputFieldName,
  });
  fld.corrections.count += 1;
  if (finalValue !== '') fld.corrections.replace_count += 1;
}

function dateValue(target) {
  return typeof target.value === 'string' ? target.value : '';
}

function noteDateCorrectionInput(field, fld, form, target) {
  if (field.type !== 'date') return;

  let session = dateCorrectionSessions.get(target);
  if (!session) {
    const valueAtFocus = typeof fld.runtime.valueAtFocus === 'string' ? fld.runtime.valueAtFocus : '';
    session = {
      baselineValue: valueAtFocus,
      baselineInputAt: fld.runtime.lastInputAt,
      previousFormInputFieldName: form.runtime.lastInputFieldName,
      sawInput: false,
    };
    dateCorrectionSessions.set(target, session);
  }
  session.sawInput = true;
}

export function resetDateCorrectionBaseline(field, fld, form, target, t) {
  if (field.type !== 'date') return;
  const session = dateCorrectionSessions.get(target);
  if (!session) return;

  session.baselineValue = dateValue(target);
  session.baselineInputAt = t;
  session.previousFormInputFieldName = field.fieldName;
  session.sawInput = false;
}

function beforeInputHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const form = getOrCreateForm(pageKey, field.formId);
  if (!fld.keystroke) return;

  const inputType = e.inputType || '';
  if (field.type !== 'date') {
    if (CORRECTION_INPUT_TYPES.has(inputType)) {
      recordCorrectionTiming(form, fld);
      fld.corrections.count += 1;
      if (inputType === 'deleteContentBackward') {
        if (!fld.runtime.backspaceBurstStarted) {
          fld.runtime.backspaceBurstStarted = true;
          fld.runtime.backspaceBurstLen = 0;
        }
        fld.runtime.backspaceBurstLen += 1;
      } else if (fld.runtime.backspaceBurstStarted) {
        welfordAdd(fld.corrections._bursts, fld.runtime.backspaceBurstLen);
        fld.runtime.backspaceBurstStarted = false;
        fld.runtime.backspaceBurstLen = 0;
      }
    } else if (fld.runtime.backspaceBurstStarted) {
      welfordAdd(fld.corrections._bursts, fld.runtime.backspaceBurstLen);
      fld.runtime.backspaceBurstStarted = false;
      fld.runtime.backspaceBurstLen = 0;
    }

    if (inputType === 'insertReplacementText') fld.corrections.replace_count += 1;
  }

  if (inputType === 'insertFromPaste' || inputType === 'insertFromPasteAsQuotation') {
    const tgt = e.target;
    const currentLen = typeof tgt.value === 'string' ? tgt.value.length : 0;
    let selectionLen = 0;
    try {
      if (typeof tgt.selectionStart === 'number' && typeof tgt.selectionEnd === 'number') {
        selectionLen = Math.max(0, tgt.selectionEnd - tgt.selectionStart);
      }
    } catch (err) {
      /* some input types disallow selection inspection */
    }
    if (currentLen > 0 && selectionLen === currentLen) {
      fld.reenter_count += 1;
      fld.reenter_by_paste_count += 1;
    }
  }
}

function inputHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const form = getOrCreateForm(pageKey, field.formId);
  const t = Date.now();

  if (field.type === 'range') {
    recordRangeValue(form, fld, field, e.target, shouldCountRangeEvent(fld.runtime, 'input', t, e.target.value));
    return;
  }
  if (field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') {
    markFieldChange(form, field.fieldName, t);
    fld.changes_count = (fld.changes_count || 0) + 1;
    fld.runtime.genericInputSeenSinceChange = true;
    return;
  }

  if (!fld.keystroke) return;
  if (field.type === 'number') {
    fld.value_looks_rounded = looksRoundedNumericValue(e.target.value);
  }
  const rt = fld.runtime;
  const tgt = e.target;
  const newLen = typeof tgt.value === 'string' ? tgt.value.length : 0;
  const prevLen = rt.lastInputValueLength;
  const autocompleteEligible = isAutocompleteEligibleField(field);

  noteDateCorrectionInput(field, fld, form, tgt);
  if (prevLen >= 0 && newLen < prevLen) {
    if (e.inputType === 'deleteContentBackward') fld.corrections.backspace_count += 1;
    if (e.inputType === 'deleteContentForward') fld.corrections.del_count += 1;
  }

  if (rt.firstInputAt === 0) {
    rt.firstInputAt = t;
    if (rt.lastFocusInAt > 0) fld.pause_before_input_ms = Math.max(0, t - rt.lastFocusInAt);
  }
  rt.lastInputAt = t;
  if (rt.focusSessions > 1 && rt.inputsThisFocus === 0) fld.revision_count += 1;
  rt.inputsThisFocus += 1;
  markFieldChange(form, field.fieldName, t);
  if (rt.firstFocusAt > 0) fld.completion_time_ms = Math.max(0, t - rt.firstFocusAt);

  if (prevLen >= 0) {
    if (prevLen > 0 && newLen === 0) rt.transitionedToEmpty = true;
    if (rt.transitionedToEmpty && newLen > 0) {
      fld.reenter_count += 1;
      rt.transitionedToEmpty = false;
    }
  }

  if (rt.pasteThisFocus && (e.inputType ? e.inputType !== 'insertFromPaste' : true)) {
    fld.edit_after_paste_count += 1;
    rt.pasteThisFocus = false;
  }

  if (autocompleteEligible) {
    const sinceLastKey = rt.lastKeyAt > 0 ? t - rt.lastKeyAt : Infinity;
    const valueJump = newLen - Math.max(0, prevLen);
    const detectsAutocompleteOnThisInput =
      valueJump >= AUTOFILL_VALUE_JUMP_MIN &&
      sinceLastKey > 50 &&
      !rt.pasteThisFocus &&
      (e.inputType === '' || e.inputType == null || e.inputType === 'insertReplacementText') &&
      canDetectAutocompleteOnInput(field, e.inputType);
    if (detectsAutocompleteOnThisInput) {
      fld.autocomplete.detected = true;
      rt.autocompleteDetectedThisFocus = true;
    }

    if (detectsAutocompleteAcceptance(fld, e)) {
      if (recordAutocompleteAcceptance(fld, e.target)) {
        startAutocompleteBatch(field.formId, e.target, t);
      }
    }
  }
  recordDateAutocompleteFromBatch(field, fld, e.target, t);
  if (rt.valueAtFocus !== null && tgt.value !== rt.valueAtFocus) rt.changedAwayFromFocusValue = true;

  fld.value_length = newLen;
  rt.lastInputValueLength = newLen;
}

function changeHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;

  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  const form = getOrCreateForm(pageKey, field.formId);
  if (recordDateAutocompleteFromBatch(field, fld, e.target, Date.now())) return;
  if (field.type !== 'range') return;
  const countChange = shouldCountRangeEvent(fld.runtime, 'change', Date.now(), e.target.value);

  recordRangeValue(form, fld, field, e.target, countChange);
}

/**
 * iOS Safari may fill date fields as a side effect of Contact AutoFill without
 * applying :-webkit-autofill or honouring an autocomplete token on that field.
 * A manual picker cannot reach this branch because it has no confirmed source
 * autofill batch in the same form.
 */
function recordDateAutocompleteFromBatch(field, fld, target, t) {
  if (field.type !== 'date') return false;
  if (!isWithinAutocompleteBatch(field.formId, target, t, AUTOFILL_BATCH_WINDOW_MS)) return false;

  fld.autocomplete.detected = true;
  fld.runtime.autocompleteDetectedThisFocus = true;
  const accepted = recordAutocompleteAcceptance(fld, target);
  if (accepted) {
    const pageKey = ensureCurrentPage();
    const form = getOrCreateForm(pageKey, field.formId);
    resetDateCorrectionBaseline(field, fld, form, target, t);
  }
  return accepted;
}

function shouldCountRangeEvent(rt, eventType, t, value) {
  const isChange = eventType === 'change';
  const currentValue = typeof value === 'string' ? value : String(value);

  if (rt.rangeGestureActive) {
    const countChange = !rt.rangeGestureCounted;
    if (countChange) rt.rangeGestureCounted = true;
    if (isChange) rt.rangeGestureChangeSeen = true;
    rt.rangeGestureCommittedValue = currentValue;
    rt.rangeInputSeenSinceChange = !isChange;
    return countChange;
  }

  const sinceGestureEnd = t - rt.rangeGestureEndedAt;
  const isRecentGestureDuplicate =
    rt.rangeGestureCounted &&
    rt.rangeGestureEndedAt > 0 &&
    sinceGestureEnd >= 0 &&
    sinceGestureEnd <= RANGE_GESTURE_DUPLICATE_GRACE_MS &&
    rt.rangeGestureCommittedValue === currentValue;
  if (isRecentGestureDuplicate) {
    rt.rangeInputSeenSinceChange = false;
    if (isChange) {
      rt.rangeGestureCounted = false;
      rt.rangeGestureChangeSeen = false;
      rt.rangeGestureEndedAt = 0;
      rt.rangeGestureCommittedValue = null;
    }
    return false;
  }

  const hadCompletedGesture =
    rt.rangeGestureCounted || rt.rangeGestureEndedAt > 0 || rt.rangeGestureCommittedValue !== null;
  rt.rangeGestureCounted = false;
  rt.rangeGestureChangeSeen = false;
  rt.rangeGestureEndedAt = 0;
  rt.rangeGestureCommittedValue = null;
  if (hadCompletedGesture) rt.rangeInputSeenSinceChange = false;
  const countChange = !rt.rangeInputSeenSinceChange;
  rt.rangeInputSeenSinceChange = !isChange;
  return countChange;
}

export function recordRangeValue(form, fld, field, target, countChange) {
  markFieldChange(form, field.fieldName);
  if (countChange) fld.changes_count = (fld.changes_count || 0) + 1;

  const max = Number(target.max);
  const val = Number(target.value);
  if (Number.isFinite(max) && max !== 0 && Number.isFinite(val)) {
    fld.value_to_max_ratio = val / max;
    fld.max_amt_flag = fld.value_to_max_ratio === 1;
  }
  fld.value_looks_rounded = looksRoundedNumericValue(target.value);
}

function detectsAutocompleteAcceptance(fld, e) {
  if (fld.runtime.autocompleteAcceptedRecorded) return false;
  if (!fld.autocomplete.detected) return false;
  if (!fld.runtime.autocompleteDetectedThisFocus) return false;

  const inputType = e.inputType;
  return inputType === '' || inputType == null || inputType === 'insertReplacementText';
}

function recordCorrectionTiming(form, fld, options) {
  const t = options && Number.isFinite(options.t) ? options.t : Date.now();
  const lastInputAt = options && Number.isFinite(options.lastInputAt) ? options.lastInputAt : fld.runtime.lastInputAt;
  if (lastInputAt > 0 && t > lastInputAt) {
    welfordAdd(fld.corrections._latency, t - lastInputAt);
  }

  if (form.runtime.firstFocusAt > 0) {
    const lastInputFieldName =
      options && Object.prototype.hasOwnProperty.call(options, 'lastInputFieldName')
        ? options.lastInputFieldName
        : form.runtime.lastInputFieldName;
    const lateCorrection = lastInputFieldName && lastInputFieldName !== fld.name;
    if (lateCorrection) form.corrections.late_count += 1;
    else form.corrections.early_count += 1;

    form.corrections.total_count += 1;
    form.corrections.late_ratio = form.corrections.late_count / form.corrections.total_count;
  }
}

function looksRoundedNumericValue(rawValue) {
  const normalized = typeof rawValue === 'string' ? rawValue.trim() : String(rawValue);
  if (normalized === '') return null;

  const value = Number(normalized);

  if (!Number.isFinite(value)) return null;
  if (value === 0) return true;

  return value % 10 === 0;
}

function pasteHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (!('paste_count' in fld)) return;
  fld.paste_count += 1;
  fld.runtime.pasteThisFocus = true;
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  document.addEventListener('beforeinput', beforeInputHandler, { passive: true, capture: true });
  document.addEventListener('input', inputHandler, { passive: true, capture: true });
  document.addEventListener('change', changeHandler, { passive: true, capture: true });
  document.addEventListener('paste', pasteHandler, { passive: true, capture: true });
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('beforeinput', beforeInputHandler, true);
  document.removeEventListener('input', inputHandler, true);
  document.removeEventListener('change', changeHandler, true);
  document.removeEventListener('paste', pasteHandler, true);
  dateCorrectionSessions = new WeakMap();
  attached = false;
}
